import { logError } from "@/lib/log";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, ImagePlus, X, Landmark, CheckCircle2, ExternalLink, Store, Bell, Printer, CalendarDays, ShoppingBasket, CreditCard } from "lucide-react";
import { OptionBanners } from "@/components/OptionBanners";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { TakeawaySettings } from "@/components/TakeawaySettings";
import { ReservationSettings } from "@/components/ReservationSettings";
import { PushNotificationsCard } from "@/components/PushNotificationsCard";
import { PrinterCard } from "@/components/PrinterCard";
import { UpgradeNotice } from "@/components/UpgradeNotice";

export default function Settings() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form, setForm] = useState(null);
  useEffect(() => { if (form && window.location.hash) setTimeout(() => document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 400); }, [form]);
  const [saving, setSaving] = useState(false);
  const [connect, setConnect] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const logoRef = useRef();
  const coverRef = useRef();
  const canPay = !!user?.subscription?.features?.pay_bill;
  const isPaid = !!user?.is_paid;
  const canTakeaway = !!user?.subscription?.features?.takeaway;

  const loadConnect = useCallback(async () => {
    if (!canPay) return;
    try { setConnect((await api.get("/connect/status")).data); }
    catch (e) { logError("Stato Stripe Connect non disponibile:", e); }
  }, [canPay]);
  useEffect(() => { loadConnect(); }, [loadConnect]);

  useEffect(() => {
    if (searchParams.get("connect")) {
      loadConnect();
      if (searchParams.get("connect") === "done") toast.success("Verifica del conto aggiornata.");
      searchParams.delete("connect");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, loadConnect]);

  const startConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setConnecting(false); }
  };

  useEffect(() => {
    (async () => {
      try { setForm((await api.get("/restaurant")).data); }
      catch (e) { logError("Caricamento ristorante fallito:", e); toast.error("Impossibile caricare le impostazioni. Riprova."); }
    })();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadImage = (field, ref) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [field]: data.path }));
      toast.success("Immagine caricata");
    } catch (err) { toast.error("Upload fallito"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/restaurant", {
        name: form.name, description: form.description, address: form.address,
        phone: form.phone, booking_contact: form.booking_contact, currency_code: "eur",
        logo_path: form.logo_path, cover_path: form.cover_path,
        ...(canTakeaway ? { takeaway: form.takeaway } : {}),
        ...(user?.subscription?.features?.tables && form.reservations ? { reservations: form.reservations } : {}),
      });
      setForm(data);
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#5aa469]" /></div>;

  return (
    <div className="space-y-8 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-display font-bold text-4xl">Impostazioni ristorante</h1>
        <p className="text-gray-500 mt-1">Personalizza le informazioni della tua attività.</p>
      </div>
      <OptionBanners testid="settings-banners" cols="grid-cols-2 sm:grid-cols-3" items={[
        { label: "Locale", hint: "Nome, contatti, logo", icon: Store, anchor: "sez-locale" },
        !!user?.subscription?.features?.ordering && { label: "Notifiche", hint: "Push ordini", icon: Bell, anchor: "sez-notifiche" },
        !!user?.subscription?.features?.tables && { label: "Stampanti", hint: "Cucina e bar", icon: Printer, anchor: "sez-stampanti" },
        !!user?.subscription?.features?.tables && { label: "Prenotazione tavoli", hint: "Orari e conferma", icon: CalendarDays, anchor: "sez-prenotazioni" },
        canTakeaway && { label: "Asporto", hint: "Orari e limiti", icon: ShoppingBasket, anchor: "sez-asporto" },
        canPay && { label: "Pagamenti", hint: "Incasso Stripe", icon: CreditCard, anchor: "sez-pagamenti" },
      ]} />

      <div id="sez-locale" className="rounded-2xl mo-card p-6 space-y-5 scroll-mt-24">
        {isPaid ? (
          <div className="grid sm:grid-cols-2 gap-4">
            <ImageField label="Logo" path={form.logo_path} inputRef={logoRef} onUpload={uploadImage("logo_path", logoRef)} onClear={() => setForm((f) => ({ ...f, logo_path: null }))} testid="logo" />
            <ImageField label="Immagine di copertina" path={form.cover_path} inputRef={coverRef} onUpload={uploadImage("cover_path", coverRef)} onClear={() => setForm((f) => ({ ...f, cover_path: null }))} testid="cover" />
          </div>
        ) : (
          <UpgradeNotice testid="free-settings-notice" text="Con il piano Free il menù mostra il logo MenuOne. Logo, copertina, indirizzo, telefono e contatti del locale sono disponibili dal piano Basic." />
        )}
        <div>
          <Label>Nome attività</Label>
          <Input value={form.name} onChange={set("name")} className="mt-1.5 h-11 rounded-lg focus:border-[#5aa469]" data-testid="set-name" />
        </div>
        <div>
          <Label>Descrizione</Label>
          <Textarea value={form.description} onChange={set("description")} className="mt-1.5 rounded-lg focus:border-[#5aa469]" placeholder="Racconta la tua cucina…" data-testid="set-desc" />
        </div>
        {isPaid && (<>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Indirizzo</Label>
            <Input value={form.address} onChange={set("address")} className="mt-1.5 h-11 rounded-lg focus:border-[#5aa469]" data-testid="set-address" />
          </div>
          <div>
            <Label>Telefono</Label>
            <Input value={form.phone} onChange={set("phone")} className="mt-1.5 h-11 rounded-lg focus:border-[#5aa469]" data-testid="set-phone" />
          </div>
        </div>
        </>)}
        <div className="grid sm:grid-cols-2 gap-4">
          {isPaid && (
          <div>
            <Label>Contatto prenotazioni</Label>
            <Input value={form.booking_contact} onChange={set("booking_contact")} className="mt-1.5 h-11 rounded-lg focus:border-[#5aa469]" placeholder="WhatsApp o telefono" data-testid="set-booking" />
          </div>
          )}
          <div>
            <Label>Valuta</Label>
            <Input value="EUR · € · Euro" disabled className="mt-1.5 h-11 rounded-lg bg-gray-50" data-testid="set-currency" />
            <p className="text-xs text-gray-400 mt-1">Prezzi del menù, pagamenti al tavolo e abbonamento MenuOne sono in euro.</p>
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold active:scale-95 transition-transform" data-testid="save-settings-btn">{saving ? "Salvataggio…" : "Salva modifiche"}</Button>
      </div>

      {!!user?.subscription?.features?.ordering && <div id="sez-notifiche" className="scroll-mt-24"><PushNotificationsCard /></div>}
      {!!user?.subscription?.features?.tables && <div id="sez-stampanti" className="scroll-mt-24"><PrinterCard restaurantName={form.name} /></div>}

      {!!user?.subscription?.features?.tables && (
        <div id="sez-prenotazioni" className="space-y-3 scroll-mt-24">
          <ReservationSettings value={form.reservations} onChange={(reservations) => setForm((f) => ({ ...f, reservations }))} />
          <Button onClick={save} disabled={saving} className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold" data-testid="save-reservations-btn">{saving ? "Salvataggio…" : "Salva prenotazioni"}</Button>
        </div>
      )}

      {canTakeaway && (
        <div id="sez-asporto" className="space-y-3 scroll-mt-24">
          <TakeawaySettings value={form.takeaway} onChange={(takeaway) => setForm((f) => ({ ...f, takeaway }))} onlinePayReady={!!connect?.charges_enabled} />
          <Button onClick={save} disabled={saving} className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold" data-testid="save-takeaway-btn">{saving ? "Salvataggio…" : "Salva asporto"}</Button>
        </div>
      )}

      {canPay && (
        <div id="sez-pagamenti" className="rounded-2xl mo-card p-6 scroll-mt-24" data-testid="connect-card">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#5aa469]/12 flex items-center justify-center shrink-0"><Landmark className="h-5 w-5 text-[#4a8a56]" /></div>
            <div className="flex-1">
              <h3 className="font-display font-semibold text-xl">Incassi — pagamento al tavolo</h3>
              <p className="text-sm text-gray-600 mt-1 mb-4">
                Collega il tuo conto Stripe per ricevere i pagamenti del conto <strong>direttamente sul conto della tua attività</strong>. Il 100% dell'importo va a te, senza passare da terzi.
              </p>
              {connect?.charges_enabled ? (
                <div className="flex items-center gap-2 text-[#4a8a56] font-medium" data-testid="connect-ready">
                  <CheckCircle2 className="h-5 w-5" /> Conto collegato e attivo per gli incassi
                </div>
              ) : (
                <div className="space-y-3">
                  {connect?.connected && !connect?.charges_enabled && (
                    <p className="text-sm text-amber-600" data-testid="connect-pending">Verifica non ancora completata. Completa i dati richiesti da Stripe per iniziare a incassare.</p>
                  )}
                  <Button onClick={startConnect} disabled={connecting} className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold" data-testid="connect-onboard-btn">
                    {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ExternalLink className="h-4 w-4 mr-2" />{connect?.connected ? "Completa la registrazione del conto" : "Collega il tuo conto per incassare"}</>}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function ImageField({ label, path, inputRef, onUpload, onClear, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1.5 flex items-center gap-3">
        <div className="h-16 w-16 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
          {path ? <img src={fileUrl(path)} alt="" className="h-full w-full object-cover" /> : <ImagePlus className="h-5 w-5 text-gray-300" />}
        </div>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid={`${testid}-input`} />
        <Button variant="outline" size="sm" className="rounded-full border-gray-300 bg-white hover:bg-gray-50" onClick={() => inputRef.current?.click()} data-testid={`${testid}-upload`}>Carica</Button>
        {path && <Button variant="ghost" size="icon" className="text-red-500 hover:bg-gray-100" onClick={onClear}><X className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
}
